import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const COLORS = ["#006747", "#2a6f8a", "#c0612f", "#7a5aa8", "#b23b6f", "#9a7b1f"];
type Routing = { domain: string | null; configured: boolean; canManage: boolean };

function AddMailbox({ routing }: { routing: Routing }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(""); // local part when a routed subdomain exists
  const [address, setAddress] = useState(""); // full address when it doesn't
  const [label, setLabel] = useState("");
  const [signoff, setSignoff] = useState("");
  const [fromName, setFromName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [color, setColor] = useState(COLORS[0]!);
  const [lastRouting, setLastRouting] = useState<{ routed: boolean; reason?: string; address: string } | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const common = { label: label.trim(), signoff: signoff.trim() || undefined, color, fromName: fromName.trim() || undefined, replyTo: replyTo.trim() || undefined };
      return routing.domain ? api.createMailbox({ local: local.trim(), ...common }) : api.createMailbox({ address: address.trim(), ...common });
    },
    onSuccess: async (r) => {
      await queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      setLastRouting({ routed: r.routing.routed, reason: r.routing.reason, address: r.address });
      setLocal(""); setAddress(""); setLabel(""); setSignoff(""); setFromName(""); setReplyTo(""); setColor(COLORS[0]!);
    },
  });
  const valid = label.trim().length > 0 && (routing.domain ? /^[a-z0-9._%+-]{1,64}$/.test(local.trim().toLowerCase()) : /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(address.trim()));

  if (!open) {
    return (
      <button onClick={() => { setOpen(true); setLastRouting(null); }} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 600, color: "#fff", background: "#006747", border: 0, borderRadius: 7, padding: "8px 13px", cursor: "pointer" }}>
        <Icon name="mail" size={14} color="#fff" />Add mailbox
      </button>
    );
  }
  const field = { width: "100%", boxSizing: "border-box", border: "1px solid #e2e0d8", borderRadius: 7, padding: "8px 10px", fontSize: 13, outline: "none" } as const;
  return (
    <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,103,71,.35)", padding: "16px 18px", maxWidth: 480 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>New mailbox</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#8a8e88" }}>Inbound address</label>
          {routing.domain ? (
            <div style={{ display: "flex", alignItems: "stretch", border: "1px solid #e2e0d8", borderRadius: 7, overflow: "hidden" }}>
              <input value={local} onChange={(e) => setLocal(e.target.value)} placeholder="support" autoFocus style={{ flex: 1, border: 0, padding: "8px 10px", fontSize: 13, outline: "none", fontFamily: "'Space Mono',monospace" }} />
              <span style={{ display: "flex", alignItems: "center", padding: "0 10px", background: "#f6f5f1", color: "#8a8e88", fontSize: 12.5, fontFamily: "'Space Mono',monospace", borderLeft: "1px solid #e2e0d8" }}>@{routing.domain}</span>
            </div>
          ) : (
            <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="support@yourclient.com" autoFocus style={field} />
          )}
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#8a8e88" }}>Label</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Client support" style={field} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#8a8e88" }}>Display name (optional)</label>
          <input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Recursive Support" style={field} />
          <div style={{ fontSize: 10.5, color: "#a8aca6", marginTop: 3 }}>Shown as the sender name on replies from this mailbox.</div>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#8a8e88" }}>Reply-to (optional)</label>
          <input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="helpdesk@tribusgroup.com" style={field} />
          <div style={{ fontSize: 10.5, color: "#a8aca6", marginTop: 3 }}>Where customer replies go (e.g. the address that forwards back into this mailbox).</div>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#8a8e88" }}>Sign-off (optional)</label>
          <input value={signoff} onChange={(e) => setSignoff(e.target.value)} placeholder="Recursive Support" style={field} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#8a8e88", display: "block", marginBottom: 5 }}>Color</label>
          <div style={{ display: "flex", gap: 7 }}>
            {COLORS.map((cc) => (
              <button key={cc} onClick={() => setColor(cc)} title={cc} style={{ width: 22, height: 22, borderRadius: "50%", background: cc, border: color === cc ? "2px solid #1d241f" : "2px solid transparent", cursor: "pointer" }} />
            ))}
          </div>
        </div>
      </div>
      {create.isError && (
        <div style={{ fontSize: 11.5, color: "#b23b3b", marginTop: 8 }}>{create.error instanceof Error ? create.error.message : "Couldn't create mailbox"}</div>
      )}
      {lastRouting && (
        <div style={{ fontSize: 11.5, marginTop: 8, padding: "7px 9px", borderRadius: 6, background: lastRouting.routed ? "#e4f1ea" : "#fbf6e9", color: lastRouting.routed ? "#1f6d47" : "#7a5f14" }}>
          {lastRouting.routed
            ? `Created ${lastRouting.address} — Cloudflare routing is live ✓`
            : `Created ${lastRouting.address}, but routing wasn't provisioned: ${lastRouting.reason ?? "email routing not configured"}.`}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button onClick={() => create.mutate()} disabled={!valid || create.isPending} style={{ fontSize: 12.5, fontWeight: 600, color: "#fff", background: "#006747", border: 0, borderRadius: 7, padding: "8px 13px", cursor: valid && !create.isPending ? "pointer" : "default", opacity: valid && !create.isPending ? 1 : 0.55 }}>
          {create.isPending ? "Creating…" : "Create mailbox"}
        </button>
        <button onClick={() => { setOpen(false); create.reset(); setLastRouting(null); }} disabled={create.isPending} style={{ fontSize: 12.5, fontWeight: 600, color: "#4a504a", background: "#fff", border: "1px solid #e2e0d8", borderRadius: 7, padding: "8px 13px", cursor: "pointer" }}>
          Done
        </button>
      </div>
    </div>
  );
}

// Per-mailbox outbound identity — the display name recipients see + the Reply-To.
function MailboxIdentity({ mb, canManage }: { mb: { id: string; fromName: string | null; replyTo: string | null }; canManage: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [fromName, setFromName] = useState(mb.fromName ?? "");
  const [replyTo, setReplyTo] = useState(mb.replyTo ?? "");
  const save = useMutation({
    mutationFn: () => api.updateMailbox(mb.id, { fromName: fromName.trim(), replyTo: replyTo.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mailboxes"] }); setEditing(false); },
  });
  const field = { flex: 1, minWidth: 0, boxSizing: "border-box", border: "1px solid #e2e0d8", borderRadius: 6, padding: "5px 8px", fontSize: 12, outline: "none" } as const;

  if (!editing) {
    return (
      <div style={{ marginTop: 6, fontSize: 11.5, color: "#8a8e88", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          Sends as <b style={{ color: "#4a504a" }}>{mb.fromName || "Recursive Support"}</b>
          {mb.replyTo ? <> · reply-to <span style={{ fontFamily: "'Space Mono',monospace" }}>{mb.replyTo}</span></> : ""}
        </span>
        {canManage && (
          <button onClick={() => setEditing(true)} style={{ fontSize: 11.5, color: "#006747", background: "none", border: 0, cursor: "pointer", flex: "none", fontWeight: 600 }}>Edit</button>
        )}
      </div>
    );
  }
  return (
    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
      <input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Display name" style={field} />
      <input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="Reply-to email (optional)" style={field} />
      {save.isError && <div style={{ fontSize: 11, color: "#b3261e" }}>{save.error instanceof Error ? save.error.message : "Couldn't save"}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => !save.isPending && save.mutate()} disabled={save.isPending} style={{ fontSize: 11.5, fontWeight: 600, color: "#fff", background: "#006747", border: 0, borderRadius: 6, padding: "5px 12px", cursor: save.isPending ? "default" : "pointer" }}>
          {save.isPending ? "Saving…" : "Save"}
        </button>
        <button onClick={() => { setEditing(false); setFromName(mb.fromName ?? ""); setReplyTo(mb.replyTo ?? ""); save.reset(); }} style={{ fontSize: 11.5, fontWeight: 600, color: "#4a504a", background: "none", border: 0, cursor: "pointer" }}>Cancel</button>
      </div>
    </div>
  );
}

function RoutingNote({ routing }: { routing: Routing }) {
  let text: string;
  let tone: { bg: string; fg: string };
  if (routing.configured && routing.domain) {
    text = `Inbound mail to ‹name›@${routing.domain} is auto-routed to the helpdesk when you add a mailbox. Forward your staff-facing address (e.g. support@solidearth.com) into it from Google Workspace so the main domain stays on Google.`;
    tone = { bg: "#e8ebf0", fg: "#4a5675" };
  } else if (routing.domain) {
    text = `Routing subdomain ${routing.domain} is set, but the Cloudflare API token isn't configured yet — mailboxes are created but NOT auto-routed until it is.`;
    tone = { bg: "#fbf6e9", fg: "#7a5f14" };
  } else {
    text = "Email routing isn't set up yet, so mailboxes are records only — no mail flows until a routed subdomain + API token are configured.";
    tone = { bg: "#fbf6e9", fg: "#7a5f14" };
  }
  return <div style={{ fontSize: 12, padding: "9px 12px", borderRadius: 6, background: tone.bg, color: tone.fg }}>{text}</div>;
}

export function Mailboxes() {
  const { data, isLoading, error } = useQuery({ queryKey: ["mailboxes"], queryFn: api.mailboxes });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  const { routing, mailboxes } = data;

  return (
    <Page>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Mailboxes & teams</h1>
          <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>
            One inbound address = one queue with a team ACL. Reply-from = the ticket’s mailbox; signature follows the mailbox.
          </div>
        </div>
        {routing.canManage && <AddMailbox routing={routing} />}
      </div>

      <RoutingNote routing={routing} />

      {mailboxes.length === 0 && (
        <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "22px", fontSize: 13, color: "#8a8e88" }}>
          No mailboxes yet — add your first inbound address to start routing tickets.
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 14 }}>
        {mailboxes.map((mb) => (
          <div key={mb.id} style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "16px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span style={{ width: 30, height: 30, borderRadius: 6, background: mb.color + "18", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                <Icon name="mail" size={15} color={mb.color} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 600 }}>{mb.label}</div>
                <div style={{ fontSize: 12, color: "#8a8e88", fontFamily: "'Space Mono',monospace" }}>{mb.address}</div>
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#a8aca6", marginBottom: 6 }}>Team ({mb.members.length})</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {mb.members.map((m) => (
                  <span key={m} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#4a504a", background: "#f6f5f1", border: "1px solid #e7e5df", borderRadius: 999, padding: "3px 9px" }}>
                    <span style={{ width: 18, height: 18, borderRadius: "50%", background: "#e7e1d4", color: "#006747", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 9 }}>
                      {m.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                    </span>
                    {m}
                  </span>
                ))}
                {mb.members.length === 0 && <span style={{ fontSize: 12, color: "#8a8e88" }}>No members</span>}
              </div>
            </div>
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #f0efe9", display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#8a8e88" }}>
              <Icon name="scroll-text" size={13} color="#8a8e88" />
              {mb.signatures} per-employee signature{mb.signatures === 1 ? "" : "s"} · sign-off “{mb.signoff}”
            </div>
            <MailboxIdentity mb={mb} canManage={routing.canManage} />
          </div>
        ))}
      </div>
    </Page>
  );
}
